/**
 * OnboardingPickerDialog Component
 *
 * Dialog for selecting an onboarding with folder navigation and search.
 * Returns the selected onboarding with its latest version information.
 */

import React, { useCallback, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Breadcrumbs,
  Link,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { OnboardingPickerTable } from './OnboardingPickerTable';
import { useOnboardingPicker } from './useOnboardingPicker';
import type { OnboardingPickerDialogProps } from './OnboardingPicker.types';

/**
 * OnboardingPickerDialog Component
 *
 * @example
 * ```tsx
 * <OnboardingPickerDialog
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   onSelect={handleSelect}
 *   projectId="project-123"
 * />
 * ```
 */
export const OnboardingPickerDialog: React.FC<OnboardingPickerDialogProps> = ({
  open,
  onClose,
  onSelect,
  projectId,
  selectedOnboardingId,
}) => {
  const theme = useTheme();
  const [selectionError, setSelectionError] = useState<string | null>(null);

  const {
    items,
    loading,
    error,
    breadcrumb,
    searchQuery,
    navigateToFolder,
    navigateToBreadcrumb,
    setSearchQuery,
    selectOnboarding,
  } = useOnboardingPicker({ projectId });

  /**
   * Handle folder click - navigate into folder
   */
  const handleFolderClick = useCallback((folderId: string, folderName: string) => {
    setSelectionError(null);
    navigateToFolder(folderId, folderName);
  }, [navigateToFolder]);

  /**
   * Handle onboarding click - select and close
   */
  const handleOnboardingClick = useCallback(async (onboardingId: string) => {
    setSelectionError(null);

    const selection = await selectOnboarding(onboardingId);

    if (selection) {
      onSelect(selection);
      onClose();
    } else {
      setSelectionError('This onboarding has no versions available');
    }
  }, [selectOnboarding, onSelect, onClose]);

  /**
   * Handle breadcrumb click
   */
  const handleBreadcrumbClick = useCallback((index: number) => {
    setSelectionError(null);
    navigateToBreadcrumb(index);
  }, [navigateToBreadcrumb]);

  /**
   * Handle search change
   */
  const handleSearchChange = useCallback((query: string) => {
    setSelectionError(null);
    setSearchQuery(query);
  }, [setSearchQuery]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          height: '70vh',
          maxHeight: 600,
        },
      }}
    >
      <DialogTitle>Select Onboarding</DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: theme.spacing(2),
          pt: `${theme.spacing(1)} !important`,
        }}
      >
        {/* Breadcrumb Navigation */}
        {breadcrumb.length > 0 && (
          <Box sx={{ mb: theme.spacing(2) }}>
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="folder navigation"
            >
              {breadcrumb.map((item, index) => {
                const isLast = index === breadcrumb.length - 1;

                if (isLast) {
                  return (
                    <Typography
                      key={item.id ?? 'root'}
                      color="text.primary"
                      variant="body2"
                    >
                      {item.name}
                    </Typography>
                  );
                }

                return (
                  <Link
                    key={item.id ?? 'root'}
                    component="button"
                    variant="body2"
                    underline="hover"
                    color="inherit"
                    onClick={() => handleBreadcrumbClick(index)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </Breadcrumbs>
          </Box>
        )}

        {/* Selection Error */}
        {selectionError && (
          <Alert severity="warning" sx={{ mb: theme.spacing(2) }}>
            {selectionError}
          </Alert>
        )}

        {/* Table */}
        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          <OnboardingPickerTable
            items={items}
            loading={loading}
            error={error}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            onFolderClick={handleFolderClick}
            onOnboardingClick={handleOnboardingClick}
            selectedOnboardingId={selectedOnboardingId}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};
